import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Shield, Building2, Activity } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
const MOTIVATIONS = ['Estate planning', 'Family changes', 'Property matters', 'Employment concerns', 'Business contracts', 'Compliance & policies', 'General legal readiness'];
export function PreferencesPage() {
  const navigate = useNavigate();
  const {
    preferences,
    updatePreferences
  } = useAuth();
  const [formData, setFormData] = useState({
    startWith: preferences?.startWith || '',
    motivations: preferences?.motivations || []
  });
  const toggleMotivation = (motivation: string) => {
    setFormData(prev => {
      const isSelected = prev.motivations.includes(motivation);
      if (isSelected) {
        return {
          ...prev,
          motivations: prev.motivations.filter(m => m !== motivation)
        };
      } else {
        return {
          ...prev,
          motivations: [...prev.motivations, motivation]
        };
      }
    });
  };
  const handleNext = () => {
    updatePreferences(formData);
    navigate('/onboarding/business');
  };
  const handleBack = () => {
    navigate('/onboarding/profile');
  };
  const isValid = formData.startWith !== '';
  return <div className="min-h-screen bg-aliceBlue flex flex-col justify-center py-12 sm:px-6 lg:px-8" data-id="element-1312">
      <div className="sm:mx-auto sm:w-full sm:max-w-xl" data-id="element-1313">
        <div className="mb-8" data-id="element-1314">
          <div className="flex justify-between items-center mb-2" data-id="element-1315">
            <span className="text-sm font-semibold text-primary" data-id="element-1316">
              Assessment Intent
            </span>
            <span className="text-sm font-medium text-secondary" data-id="element-1317">Step 2</span>
          </div>
          <div className="w-full bg-borderColor rounded-full h-2" data-id="element-1318">
            <div className="bg-violetBlue h-2 rounded-full transition-all duration-300" style={{
            width: '50%'
          }} data-id="element-1319"></div>
          </div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white py-8 px-6 shadow-sm sm:rounded-xl sm:px-10 border border-borderColor" data-id="element-1320">
          <div className="space-y-8" data-id="element-1321">
            <div data-id="element-1322">
              <label className="block text-sm font-medium text-primary mb-4" data-id="element-1323">
                What would you like to start with?
              </label>
              <div className="space-y-3" data-id="element-1324">
                <button onClick={() => setFormData({
                ...formData,
                startWith: 'personal'
              })} className={`w-full flex items-center p-4 rounded-xl border-2 text-left transition-colors ${formData.startWith === 'personal' ? 'border-turquoise bg-aliceBlue' : 'border-borderColor hover:border-turquoise/50'}`} data-id="element-1325">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-4 ${formData.startWith === 'personal' ? 'bg-turquoise text-white' : 'bg-aliceBlue text-turquoise'}`} data-id="element-1326">
                    <Shield className="w-5 h-5" data-id="element-1327" />
                  </div>
                  <div data-id="element-1328">
                    <h4 className="font-bold text-primary" data-id="element-1329">
                      Personal Legal Health Check
                    </h4>
                    <p className="text-sm text-secondary" data-id="element-1330">
                      Focus on family, estate, and property
                    </p>
                  </div>
                </button>

                <button onClick={() => setFormData({
                ...formData,
                startWith: 'business'
              })} className={`w-full flex items-center p-4 rounded-xl border-2 text-left transition-colors ${formData.startWith === 'business' ? 'border-violetBlue bg-aliceBlue' : 'border-borderColor hover:border-violetBlue/50'}`} data-id="element-1331">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-4 ${formData.startWith === 'business' ? 'bg-violetBlue text-white' : 'bg-aliceBlue text-violetBlue'}`} data-id="element-1332">
                    <Building2 className="w-5 h-5" data-id="element-1333" />
                  </div>
                  <div data-id="element-1334">
                    <h4 className="font-bold text-primary" data-id="element-1335">
                      Business Legal Health Check
                    </h4>
                    <p className="text-sm text-secondary" data-id="element-1336">
                      Focus on contracts, HR, and compliance
                    </p>
                  </div>
                </button>

                <button onClick={() => setFormData({
                ...formData,
                startWith: 'both'
              })} className={`w-full flex items-center p-4 rounded-xl border-2 text-left transition-colors ${formData.startWith === 'both' ? 'border-primary bg-aliceBlue' : 'border-borderColor hover:border-primary/50'}`} data-id="element-1337">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-4 ${formData.startWith === 'both' ? 'bg-primary text-white' : 'bg-aliceBlue text-primary'}`} data-id="element-1338">
                    <Activity className="w-5 h-5" data-id="element-1339" />
                  </div>
                  <div data-id="element-1340">
                    <h4 className="font-bold text-primary" data-id="element-1341">
                      I want access to both
                    </h4>
                    <p className="text-sm text-secondary" data-id="element-1342">
                      Complete the full comprehensive assessment
                    </p>
                  </div>
                </button>
              </div>
            </div>

            <div data-id="element-1343">
              <label className="block text-sm font-medium text-primary mb-3" data-id="element-1344">
                What areas are you most interested in?{' '}
                <span className="text-secondary font-normal" data-id="element-1345">(Optional)</span>
              </label>
              <div className="flex flex-wrap gap-2" data-id="element-1346">
                {MOTIVATIONS.map(motivation => {
                const isSelected = formData.motivations.includes(motivation);
                return <button key={motivation} onClick={() => toggleMotivation(motivation)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors border ${isSelected ? 'bg-violetBlue text-white border-violetBlue' : 'bg-white text-secondary border-borderColor hover:bg-aliceBlue hover:text-primary'}`} data-id="element-1347">
                      {motivation}
                    </button>;
              })}
              </div>
            </div>
          </div>

          <div className="mt-10 flex justify-between gap-4" data-id="element-1348">
            <button onClick={handleBack} className="flex items-center px-6 py-3 border border-borderColor rounded-lg text-sm font-semibold text-primary hover:bg-aliceBlue transition-colors" data-id="element-1349">
              <ChevronLeft className="w-4 h-4 mr-1" data-id="element-1350" /> Back
            </button>
            <button onClick={handleNext} disabled={!isValid} className="flex items-center px-8 py-3 bg-violetBlue text-white rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity" data-id="element-1351">
              Next <ChevronRight className="w-4 h-4 ml-1" data-id="element-1352" />
            </button>
          </div>
        </motion.div>
      </div>
    </div>;
}